import Navigation from "@/components/Navigation";
import Footer from "@/components/Footer";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar";
import { Checkbox } from "@/components/ui/checkbox";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from "@/components/ui/dialog";
import { Calendar, MapPin, Package, User, Mail, Phone, Home, Camera, Bell, Shield, LogOut, PartyPopper, CheckCircle2 } from "lucide-react";
import FittedCoverPreview from "@/components/shared/yearbook-preview/FittedCoverPreview";
import { useLocation } from "react-router-dom";
import ProfileTabs from "@/components/ProfileTabs";
import { useEffect, useRef, useState } from "react";

/** Jeton CSRF Rails injecté dans le <head> */
function CsrfInput() {
  const token =
    typeof document !== "undefined"
      ? document.querySelector<HTMLMetaElement>('meta[name="csrf-token"]')?.content ?? ""
      : "";
  return <input type="hidden" name="authenticity_token" value={token} />;
}


interface DeliveryAddress {
  name: string;
  address: string;
  city: string;
}

interface Order {
  id: number;
  title: string;
  image: string;
  coverPage?: unknown;
  aspectRatio?: number;
  orderDate?: string;
  deliveryDate?: string;
  receivedDate?: string;
  estimatedDelivery?: string;
  status?: string;
  deliveryStatus?: string;
  quantity: number;
  total: string;
  description: string;
  deliveryAddress: DeliveryAddress;
  renewLink?: string;
  showAlbumUrl?: string;
}

interface Address {
  firstname: string;
  lastname: string;
  street: string;
  street2: string;
  zip: string;
  city: string;
  country: string;
  tel: string;
}

interface Profile {
  id?: number | string;
  name: string;
  firstName: string;
  lastName: string;
  email: string;
  phone: string;
  address: string;
  city: string;
  country: string;
  joinDate: string;
  avatar: string;
  totalOrders: number;
  membershipLevel: string;
  newletter?: boolean;
  addressId?: number | string;
  addressData?: Address;
}

const defaultOrdersData: Order[] = [
  {
    id: 1,
    title: "Grèce 2024",
    image: "/images/occasions/voyage1.jpg",
    orderDate: "18/10/2024 09:54",
    deliveryDate: "21/10/2024 09:45",
    receivedDate: "25/10/2024",
    estimatedDelivery: "25/10/2024 et le 29/10/2024",
    status: "Album commandé",
    deliveryStatus: "Album livré",
    quantity: 1,
    total: "0.0€",
    description: "Album rigide • Pix A4 • 24 pages",
    deliveryAddress: {
      name: "Hubert Dalstein",
      address: "198 avenue de France",
      city: "75014 paris France"
    },
    showAlbumUrl: "/social_networks/1/editor",
    renewLink: "/orders/1/renew"
  },
  {
    id: 2,
    title: "Naissance (copie)",
    image: "/images/occasions/naissance1.jpg",
    orderDate: "25/09/2024 11:14",
    deliveryDate: "14/10/2024 17:02",
    estimatedDelivery: "02/10/2024 et le 06/10/2024",
    status: "Album commandé",
    deliveryStatus: "Album livré",
    quantity: 1,
    total: "0.0€",
    description: "Album rigide • Pix A4 • 32 pages",
    deliveryAddress: {
      name: "Hubert Dalstein",
      address: "198 avenue de France",
      city: "75014 Paris France"
    },
    showAlbumUrl: "/social_networks/2/editor"
  }
];

const defaultProfileData: Profile = {
  name: "Hubert Dalstein",
  firstName: "Hubert",
  lastName: "Dalstein",
  email: "hubert@example.com",
  phone: "+33 1 23 45 67 89",
  address: "198 avenue de France",
  city: "75014 Paris",
  country: "France",
  joinDate: "Membre depuis septembre 2024",
  avatar: "/images/team/hubert.png",
  totalOrders: 15,
  membershipLevel: "Premium",
  newletter: false,
  addressId: 1,
  addressData: {
    firstname: "Hubert",
    lastname: "Dalstein",
    street: "198 avenue de France",
    street2: "",
    zip: "75014",
    city: "Paris",
    country: "France",
    tel: "+33 1 23 45 67 89"
  }
};

const sections = [
  { key: "profile", label: "Mon profil", path: "/profiles/new", jsonUrl: "/profiles/new.json" },
  { key: "orders", label: "Mes commandes", path: "/profiles/commandes", jsonUrl: "/profiles/commandes.json" },
  { key: "avantages", label: "Mes avantages", path: "/profiles/avantages" },
];

function useActiveSection() {
  const location = useLocation();
  const match = sections.find((s) => s.path === location.pathname);
  return match ?? sections[0];
}

/**
 * Charge les données JSON d'une section (GET <path>.json).
 * En cas d'erreur (endpoint absent en dev), on retombe sur les données par défaut.
 */
function useSectionJson<T>(url: string, fallback: T, enabled = true): { data: T; loading: boolean } {
  const [data, setData] = useState<T>(fallback);
  const [loading, setLoading] = useState(enabled);

  useEffect(() => {
    if (!enabled) return;
    let cancelled = false;
    setLoading(true);
    fetch(url)
      .then((res) => {
        if (!res.ok) throw new Error(String(res.status));
        return res.json();
      })
      .then((json) => {
        if (cancelled) return;
        setData(Array.isArray(json) || typeof json !== "object" || json === null ? json : { ...fallback, ...json });
      })
      .catch(() => {
        if (!cancelled) setData(fallback);
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [url, enabled]);

  return { data, loading };
}

function Loading() {
  return <p className="text-center text-muted-foreground py-12">Chargement…</p>;
}

function statusDot(status: string) {
  const s = (status || "").toLowerCase();
  if (s.includes("livr")) return "bg-green-500";
  if (s.includes("annul")) return "bg-destructive";
  return "bg-primary";
}

function OrderCard({ order }: { order: Order }) {
  return (
    <Card className="overflow-hidden border-border/50 bg-gradient-to-br from-card to-accent/10 shadow-card hover:shadow-elegant transition-smooth">
      <CardContent className="p-0">
        <div className="flex flex-col sm:flex-row">
          {/* Image + statut */}
          <div className="relative sm:w-48 md:w-56 shrink-0">
            {order.coverPage ? (
              <div className="flex items-center justify-center h-44 sm:h-full w-full bg-muted/30 p-2">
                <FittedCoverPreview
                  className="overflow-hidden rounded-lg border border-border/40 shadow-2xl w-full"
                  style={{ aspectRatio: String(Number(order.aspectRatio) > 0 ? Number(order.aspectRatio) : 1), maxHeight: "100%" }}
                  page={order.coverPage}
                  assetsBaseUrl="https://www.wememberz.com"
                  aspectRatio={order.aspectRatio}
                />
              </div>
            ) : (
              <img
                src={order.image}
                alt={order.title}
                className="h-44 sm:h-full w-full object-cover"
              />
            )}
            <div className="absolute inset-0 sm:bg-gradient-to-r sm:from-black/15 sm:to-transparent pointer-events-none" />
          </div>

          {/* Corps */}
          <div className="flex-1 p-5 md:p-6 flex flex-col gap-4">
            <div className="flex items-start justify-between gap-3">
              <div>
                <h3 className="text-xl font-display font-bold text-foreground leading-tight">{order.title}</h3>
                <p className="text-sm text-muted-foreground mt-1">{order.description}</p>
              </div>
              <div className="text-right shrink-0">
                <p className="text-[0.65rem] uppercase tracking-wide text-muted-foreground">Total</p>
                <p className="font-semibold text-foreground">{order.total}</p>
                <p className="text-xs text-muted-foreground">×{order.quantity}</p>
              </div>
            </div>

            {/* Mini-timeline commandé / impression / livré */}
            {(order.orderDate || order.deliveryDate || order.receivedDate) && (
              <div className="flex items-stretch gap-2">
                {order.orderDate && (
                  <div className="flex-1 rounded-lg bg-muted/40 p-3">
                    <div className="flex items-center gap-2 mb-1">
                      <span className="h-2 w-2 rounded-full bg-primary" />
                      <span className="text-[0.65rem] font-semibold uppercase tracking-wide text-muted-foreground">Album commandé</span>
                    </div>
                    <p className="text-sm font-medium text-foreground">{order.orderDate}</p>
                  </div>
                )}
                {order.deliveryDate && (
                  <div className="flex-1 rounded-lg bg-muted/40 p-3">
                    <div className="flex items-center gap-2 mb-1">
                      <span className="h-2 w-2 rounded-full bg-amber-500" />
                      <span className="text-[0.65rem] font-semibold uppercase tracking-wide text-muted-foreground">Album envoyé en impression</span>
                    </div>
                    <p className="text-sm font-medium text-foreground">{order.deliveryDate}</p>
                  </div>
                )}
                {order.receivedDate && (
                  <div className="flex-1 rounded-lg bg-muted/40 p-3">
                    <div className="flex items-center gap-2 mb-1">
                      <span className="h-2 w-2 rounded-full bg-green-500" />
                      <span className="text-[0.65rem] font-semibold uppercase tracking-wide text-muted-foreground">Album livré</span>
                    </div>
                    <p className="text-sm font-medium text-foreground">{order.receivedDate}</p>
                  </div>
                )}
              </div>
            )}

            {order.estimatedDelivery && (
              <p className="text-xs text-muted-foreground flex items-center gap-1.5">
                <Calendar className="h-3.5 w-3.5" />
                Livraison estimée : {order.estimatedDelivery}
              </p>
            )}

            {/* Adresse + actions */}
            <div className="flex flex-col sm:flex-row sm:items-end gap-4 pt-3 border-t border-border/50">
              <div className="flex-1 flex items-start gap-2 text-sm">
                <MapPin className="h-4 w-4 text-primary mt-0.5 shrink-0" />
                <div className="text-muted-foreground">
                  <p className="font-medium text-foreground">{order.deliveryAddress.name}</p>
                  <p>{order.deliveryAddress.address}</p>
                  <p>{order.deliveryAddress.city}</p>
                </div>
              </div>
              <div className="flex flex-row sm:flex-col gap-2 sm:w-44">
                {order.showAlbumUrl && (
                  <a href={order.showAlbumUrl} className="flex-1">
                    <Button variant="default" className="w-full">Voir mon album</Button>
                  </a>
                )}
                {order.renewLink && (
                  <a href={order.renewLink} className="flex-1">
                    <Button variant="outline" className="w-full">Renouveler</Button>
                  </a>
                )}
              </div>
            </div>
          </div>
        </div>
      </CardContent>
    </Card>
  );
}

function ProfileSection({ profile }: { profile: Profile }) {
  const avatarFormRef = useRef<HTMLFormElement>(null);
  const avatarInputRef = useRef<HTMLInputElement>(null);
  const newsletterFormRef = useRef<HTMLFormElement>(null);
  const [newsletter, setNewsletter] = useState<boolean>(!!profile.newletter);
  const [addressOpen, setAddressOpen] = useState(false);

  useEffect(() => {
    setNewsletter(!!profile.newletter);
  }, [profile.newletter]);

  const address = profile.addressData ?? {
    firstname: profile.firstName,
    lastname: profile.lastName,
    street: profile.address,
    street2: "",
    zip: profile.city?.split(" ")[0] ?? "",
    city: profile.city?.split(" ").slice(1).join(" ") ?? "",
    country: profile.country,
    tel: profile.phone,
  };

  const openFilePicker = () => avatarInputRef.current?.click();

  return (
    <div className="space-y-6">
      {/* Formulaire d'upload d'avatar (multipart, redirection côté serveur) */}
      <form
        ref={avatarFormRef}
        action="/profiles/add_principal_avatar"
        method="post"
        encType="multipart/form-data"
        className="hidden"
      >
        <CsrfInput />
        <input
          ref={avatarInputRef}
          type="file"
          name="user[avatar]"
          accept="image/*"
          onChange={() => avatarFormRef.current?.submit()}
        />
      </form>

      {/* Header Profile Card */}
      <Card className="backdrop-blur-sm bg-gradient-to-br from-card/90 to-accent/20 border-primary/10 shadow-elegant">
        <CardContent className="p-8">
          <div className="flex flex-col md:flex-row items-center gap-8">
            <div className="relative">
              <button type="button" onClick={openFilePicker} aria-label="Changer ma photo">
                <Avatar className="w-32 h-32 border-4 border-primary/20 shadow-lg cursor-pointer">
                  <AvatarImage src={profile.avatar} alt={profile.name} />
                  <AvatarFallback className="text-2xl font-bold bg-gradient-to-br from-primary to-secondary text-white">
                    {profile.firstName?.[0]}{profile.lastName?.[0]}
                  </AvatarFallback>
                </Avatar>
              </button>
              <Button
                size="icon"
                variant="secondary"
                onClick={openFilePicker}
                className="absolute -bottom-2 -right-2 rounded-full w-10 h-10 shadow-lg"
              >
                <Camera className="h-4 w-4" />
              </Button>
            </div>

            <div className="flex-1 text-center md:text-left">
              <h2 className="text-3xl font-bold text-foreground mb-2">{profile.name}</h2>
              <div className="flex flex-col md:flex-row items-center gap-4 mb-4">
                <Badge variant="secondary" className="bg-primary/10 text-primary border-primary/20">
                  {profile.membershipLevel}
                </Badge>
                <p className="text-muted-foreground">{profile.joinDate}</p>
              </div>
              <div className="flex flex-col md:flex-row items-center gap-6 text-sm text-muted-foreground">
                <div className="flex items-center gap-2">
                  <Package className="h-4 w-4 text-primary" />
                  <span>{profile.totalOrders} commandes</span>
                </div>
                <div className="flex items-center gap-2">
                  <Mail className="h-4 w-4 text-primary" />
                  <span>{profile.email}</span>
                </div>
              </div>
            </div>
          </div>
        </CardContent>
      </Card>

      <div className="grid md:grid-cols-2 gap-6">
        <Card className="backdrop-blur-sm bg-card/80 border-border/50 hover:shadow-card transition-smooth">
          <CardHeader className="pb-4">
            <CardTitle className="text-xl flex items-center gap-2">
              <User className="h-5 w-5 text-primary" />
              Informations personnelles
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="space-y-3">
              <div className="flex items-center gap-3 p-3 rounded-lg bg-muted/30">
                <Mail className="h-4 w-4 text-primary" />
                <div>
                  <label className="text-xs font-medium text-muted-foreground uppercase tracking-wide">Email</label>
                  <p className="text-foreground font-medium">{profile.email}</p>
                </div>
              </div>
              <div className="flex items-center gap-3 p-3 rounded-lg bg-muted/30">
                <Phone className="h-4 w-4 text-primary" />
                <div>
                  <label className="text-xs font-medium text-muted-foreground uppercase tracking-wide">Téléphone</label>
                  <p className="text-foreground font-medium">{profile.phone}</p>
                </div>
              </div>
            </div>
          </CardContent>
        </Card>

        <Card className="backdrop-blur-sm bg-card/80 border-border/50 hover:shadow-card transition-smooth">
          <CardHeader className="pb-4">
            <CardTitle className="text-xl flex items-center gap-2">
              <Home className="h-5 w-5 text-primary" />
              Adresse de livraison
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="p-4 rounded-lg bg-gradient-to-br from-muted/20 to-accent/10 border border-border/50">
              <div className="space-y-2">
                <p className="font-medium text-foreground">{address.firstname} {address.lastname}</p>
                <p className="text-muted-foreground">{address.street}</p>
                {address.street2 && <p className="text-muted-foreground">{address.street2}</p>}
                <p className="text-muted-foreground">{address.zip} {address.city}, {address.country}</p>
              </div>
            </div>
            <Dialog open={addressOpen} onOpenChange={setAddressOpen}>
              <DialogTrigger asChild>
                <Button variant="outline" size="sm" className="w-full gap-2">
                  <MapPin className="h-4 w-4" />
                  Modifier l'adresse
                </Button>
              </DialogTrigger>
              <DialogContent className="sm:max-w-lg">
                <DialogHeader>
                  <DialogTitle>Modifier l'adresse</DialogTitle>
                </DialogHeader>
                <form
                  action={profile.addressId ? `/addresses/${profile.addressId}` : "/addresses"}
                  method="post"
                  className="space-y-4"
                >
                  <CsrfInput />
                  {profile.addressId && <input type="hidden" name="_method" value="patch" />}
                  <div className="grid grid-cols-2 gap-4">
                    <div className="space-y-1.5">
                      <Label htmlFor="addr-firstname">Prénom</Label>
                      <Input id="addr-firstname" name="address[firstname]" defaultValue={address.firstname} />
                    </div>
                    <div className="space-y-1.5">
                      <Label htmlFor="addr-lastname">Nom</Label>
                      <Input id="addr-lastname" name="address[lastname]" defaultValue={address.lastname} />
                    </div>
                  </div>
                  <div className="space-y-1.5">
                    <Label htmlFor="addr-street">Adresse</Label>
                    <Input id="addr-street" name="address[street]" defaultValue={address.street} />
                  </div>
                  <div className="space-y-1.5">
                    <Label htmlFor="addr-street2">Complément d'adresse</Label>
                    <Input id="addr-street2" name="address[street2]" defaultValue={address.street2} />
                  </div>
                  <div className="grid grid-cols-2 gap-4">
                    <div className="space-y-1.5">
                      <Label htmlFor="addr-zip">Code postal</Label>
                      <Input id="addr-zip" name="address[zip]" defaultValue={address.zip} />
                    </div>
                    <div className="space-y-1.5">
                      <Label htmlFor="addr-city">Ville</Label>
                      <Input id="addr-city" name="address[city]" defaultValue={address.city} />
                    </div>
                  </div>
                  <div className="grid grid-cols-2 gap-4">
                    <div className="space-y-1.5">
                      <Label htmlFor="addr-country">Pays</Label>
                      <Input id="addr-country" name="address[country]" defaultValue={address.country} />
                    </div>
                    <div className="space-y-1.5">
                      <Label htmlFor="addr-tel">Téléphone</Label>
                      <Input id="addr-tel" name="address[tel]" defaultValue={address.tel} />
                    </div>
                  </div>
                  <DialogFooter>
                    <Button type="submit" variant="default">Enregistrer</Button>
                  </DialogFooter>
                </form>
              </DialogContent>
            </Dialog>
          </CardContent>
        </Card>
      </div>

      <div className="grid md:grid-cols-3 gap-6">
        <Card className="backdrop-blur-sm bg-card/80 border-border/50 hover:shadow-card transition-smooth">
          <CardHeader className="pb-4">
            <CardTitle className="text-lg flex items-center gap-2">
              <Bell className="h-5 w-5 text-primary" />
              Notifications
            </CardTitle>
          </CardHeader>
          <CardContent>
            <form ref={newsletterFormRef} action={`/profiles/${profile.id ?? ""}`} method="post" className="space-y-4">
              <CsrfInput />
              <input type="hidden" name="_method" value="patch" />
              <input type="hidden" name="user[newletter]" value={newsletter ? "true" : "false"} />
              <div className="flex items-start gap-3">
                <Checkbox
                  id="newletter"
                  checked={newsletter}
                  onCheckedChange={(v) => setNewsletter(v === true)}
                />
                <Label htmlFor="newletter" className="text-sm leading-snug cursor-pointer">
                  Je souhaite recevoir la newsletter
                </Label>
              </div>
              <Button type="submit" variant="outline" size="sm" className="w-full">
                Enregistrer
              </Button>
            </form>
          </CardContent>
        </Card>

        <Card className="backdrop-blur-sm bg-card/80 border-border/50 hover:shadow-card transition-smooth">
          <CardHeader className="pb-4">
            <CardTitle className="text-lg flex items-center gap-2">
              <Shield className="h-5 w-5 text-primary" />
              Sécurité
            </CardTitle>
          </CardHeader>
          <CardContent>
            <form action="/users/password" method="post" className="space-y-4">
              <CsrfInput />
              <input type="hidden" name="user[email]" value={profile.email} />
              <p className="text-sm text-muted-foreground">
                Envoyer les instructions de changement de mot de passe à l'adresse email {profile.email} ?
              </p>
              <Button type="submit" variant="outline" size="sm" className="w-full">
                Changer mot de passe
              </Button>
            </form>
          </CardContent>
        </Card>

        <Card className="backdrop-blur-sm bg-card/80 border-border/50 hover:shadow-card transition-smooth">
          <CardHeader className="pb-4">
            <CardTitle className="text-lg flex items-center gap-2">
              <LogOut className="h-5 w-5 text-destructive" />
              Session
            </CardTitle>
          </CardHeader>
          <CardContent>
            <form action="/users/sign_out" method="post">
              <CsrfInput />
              <input type="hidden" name="_method" value="delete" />
              <Button type="submit" variant="destructive" size="sm" className="w-full">
                Se déconnecter
              </Button>
            </form>
          </CardContent>
        </Card>
      </div>
    </div>
  );
}


export default function Commandes() {
  const current = useActiveSection();
  const location = useLocation();
  const [thankYouOpen, setThankYouOpen] = useState(false);

  const { data: profileData, loading: profileLoading } = useSectionJson<Profile>("/profiles/new.json", defaultProfileData, current.key === "profile");
  const { data: ordersData, loading: ordersLoading } = useSectionJson<Order[]>("/profiles/commandes.json", defaultOrdersData, current.key === "orders");

  // Popup de remerciement automatique quand ?order=true est dans l'URL
  useEffect(() => {
    const params = new URLSearchParams(location.search);
    if (params.get("order") === "true") {
      setThankYouOpen(true);
    }
  }, [location.search]);

  return (
    <div className="min-h-screen bg-gradient-to-br from-background via-muted/20 to-accent/10">
      <Navigation />
      
      <main className="container mx-auto px-4 pt-32 pb-16">
        <div className="max-w-6xl mx-auto">
          {/* Popup de remerciement après commande */}
          <Dialog open={thankYouOpen} onOpenChange={setThankYouOpen}>
            <DialogContent className="sm:max-w-md text-center">
              <div className="flex flex-col items-center gap-4 py-2">
                <div className="mx-auto flex h-16 w-16 items-center justify-center rounded-full bg-gradient-to-br from-primary to-accent shadow-lg">
                  <PartyPopper className="h-8 w-8 text-white" />
                </div>
                <DialogHeader className="text-center sm:text-center">
                  <DialogTitle className="text-2xl font-display text-center">
                    Merci pour votre commande !
                  </DialogTitle>
                </DialogHeader>
                <p className="text-sm text-muted-foreground leading-relaxed">
                  Votre commande a bien été enregistrée. Vous pouvez retrouver ici
                  toutes les informations relatives à votre album : statut, suivi de
                  livraison et adresse d'expédition.
                </p>
                <div className="flex items-center gap-2 text-sm text-primary font-medium">
                  <CheckCircle2 className="h-4 w-4" />
                  Consultez cette page pour suivre l'avancement
                </div>
                <DialogFooter className="w-full sm:justify-center">
                  <Button variant="default" className="w-full" onClick={() => setThankYouOpen(false)}>
                    Voir mes commandes
                  </Button>
                </DialogFooter>
              </div>
            </DialogContent>
          </Dialog>

          <h1 className="text-4xl font-bold text-center mb-8 bg-gradient-to-r from-primary to-accent bg-clip-text text-transparent">
            {current.label}
          </h1>
          
          {/* Navigation entre les 3 sections */}
          <ProfileTabs active={current.key} />

          
          {current.key === "profile" && (
            profileLoading ? <Loading /> : <ProfileSection profile={profileData} />
          )}

          
          {current.key === "orders" && (
            ordersLoading ? <Loading /> : (
            <div className="space-y-5">
              <p className="text-sm text-muted-foreground">
                {ordersData.length} commande{ordersData.length > 1 ? "s" : ""}
              </p>
              {ordersData.length === 0 ? (
                <Card className="border-border/50 bg-card/80">
                  <CardContent className="py-16 text-center">
                    <Package className="h-10 w-10 text-muted-foreground/40 mx-auto mb-3" />
                    <p className="text-muted-foreground">Aucune commande pour le moment.</p>
                  </CardContent>
                </Card>
              ) : (
                ordersData.map((order) => <OrderCard key={order.id} order={order} />)
              )}
            </div>
            )
          )}
          
        </div>
      </main>
      
      <Footer />
    </div>
  );
}